import { useEffect, useRef, useState } from 'react'
import { TURNSTILE_ENABLED, loadTurnstile } from '@/tools/submit'
import { TURNSTILE_SITE_KEY } from '@/lib/site'
import { CONTACT_FALLBACK_EMAIL } from '@/lib/contact'
import { FORM_MESSAGE_MAX } from '@/lib/formLimits'
import { useI18n } from '@/i18n/I18nProvider'

/**
 * The contact form's fields, labels and small print.
 *
 * Shared by the home page's contact block (`case 'form'` in ContentPage.tsx), by
 * the closing band at the bottom of every other page (`ContactBand`) and by the
 * imported Contact Us page (LegacyPage.tsx). One component because there is one
 * form: two copies of these fields would drift the first time somebody added a
 * field to one of them.
 *
 * **It now submits.** Asked for on 2026-08-30, explicitly without waiting for
 * the HubSpot form id ("BUNU YAP HUBSPOT ID SI OLMASA BILE YAP … ŞU AN SİTEDE
 * HAZIR OLSUN"). So the fields validate, the button works, and submitting swaps
 * the fields for the thank-you panel the request dictates word for word. What it
 * does NOT do is post anywhere: there is no endpoint yet, and inventing one here
 * would send leads somewhere nobody reads. When the HubSpot id and the mail
 * workflow arrive, `handleSubmit` is the one place that has to change, and it
 * fixes every instance of the form at once.
 *
 * **The JavaScript is an enhancement, never the render.** 117 of the site's 154
 * documents are imported pages with no client bundle, so this component still
 * renders completely as static HTML: the pickers open, answer and close on CSS
 * alone (see `pickerCss`), the "Other" box opens on a CSS `:has()`, and the
 * form carries `method="dialog"` so that a submit on a page that never hydrated
 * does nothing rather than reloading the page with the answers in the URL.
 *
 * **"Basamıyorum" — the button that did nothing** (2026-08-30). That fallback
 * was doing its job a little too well: a report page is prerendered and never
 * hydrated (`src/prerender.tsx`), so `handleSubmit` below never ran there and
 * `method="dialog"` swallowed the click in silence. Every form at the foot of a
 * report, an e-book or an imported page was a dead button, with no error and
 * nothing in the console to explain it. So submitting is now done twice over:
 * React's `handleSubmit` where the page hydrates, and `SUBMIT_JS` — an inline
 * script emitted with the form, exactly as `PICKER_JS` is — everywhere else.
 * The two never collide, because the inline handler bails the moment it sees
 * `defaultPrevented`, which React's handler sets on its first line.
 */
export interface ContactFormContent {
  fields: {
    name: string
    label: string
    /**
     * `checkboxes` is the one that takes more than one answer — a set of boxes
     * under a single question, any number of which may be ticked. Added for the
     * Partner Program form on 2026-08-30 ("3 partnerlik seçeneğinin
     * işaretlenebilir olduğu, 1'den fazla işaretlenebilir bir kısım"), where the
     * three tiers described further up the page are genuinely not exclusive.
     *
     * `required` on one of these means **at least one box**, which is a rule no
     * browser enforces natively — a `required` checkbox insists on *itself*. It
     * is checked the same way a `select`'s is: in `handleSubmit` where the page
     * hydrates, and in `SUBMIT_JS` where it does not.
     */
    type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkboxes'
    required?: boolean
    /** `select` only: the grey first option, before anything is chosen. */
    placeholder?: string
    /** `select` and `checkboxes`. Absent leaves a `select`'s placeholder alone. */
    options?: string[]
    /**
     * `select` only: an extra last option that reveals a free-text box under
     * the field when it is chosen, for an answer the list does not carry.
     * `label` is the option itself ("Other"), `placeholder` the grey prompt
     * inside the box that appears.
     */
    other?: { label: string; placeholder?: string }
  }[]
  consent: string
  submit: string
  confirmation: string
  /**
   * The data-protection sentence under the consent box, with one link in the
   * middle of it. Optional: the home page's block does not carry it, and a
   * paragraph of small print is the kind of thing a page opts into.
   */
  privacy?: {
    before: string
    link: { label: string; href: string }
    after: string
  }
  /**
   * What the reader is told after they submit. Two answers, because the same
   * form is asked two different questions: a gated PDF ("we have emailed you the
   * link") and an ordinary enquiry ("we will get back to you"). Which one shows
   * is decided by what they clicked to get here — see `pendingDownload`.
   *
   * Optional, and English below is the fallback, so a locale that has not
   * written its own still gets a sentence rather than a blank box.
   */
  thanks?: ContactFormThanks
}

export interface ContactFormThanks {
  /** After a submit that followed a Download button. */
  download: string[]
  /** After every other submit — Book a call, or the form on its own. */
  contact: string[]
  /** The closing line, with "contact us" as the link in the middle of it. */
  help: { before: string; link: string; after: string; href: string }
  /** The button that hands over the file the reader came for. */
  downloadLabel: string
  /** Shown against a picker somebody left on "Please Select". */
  requiredNote: string
  /**
   * Shown when Turnstile has produced no token, so the form refuses to submit.
   *
   * Optional, with an English fallback, because every content file already
   * supplies this object and none of them should have to be edited to gain a
   * message that a correctly configured site never shows. Added 2026-09-02:
   * before it, a tokenless submit was SENT, refused by the Worker, and
   * reported as "we could not record that automatically" — which describes a
   * database problem and offers the reader nothing to do. Kagan, looking at
   * exactly that: *"görünse ve tıklamasam göndermeme izin vermemen gerekmiyor
   * mu?"* Yes.
   */
  checkNote?: string
}

/**
 * The two lines about the bot check, per locale. Fatih, 2026-09-28: the check
 * line was English on /tr pages, because no content file supplies `checkNote`
 * and the fallback was the only copy. Keyed here rather than in each content
 * file for that same reason. Any other locale falls back to English.
 */
const CHECK_COPY: Record<string, { note: string; verifying: string }> = {
  // What the reader sees when 8 s pass with no token — Fatih's wording of
  // 2026-09-28, which names a way out that does not depend on the check
  // working at all. The address is the shared last-resort one, not a literal:
  // it is the one place a bounce costs a lead (see lib/contact.ts).
  en: {
    note: `Verification could not be completed. Please reload the page or write to ${CONTACT_FALLBACK_EMAIL}.`,
    verifying: 'Verifying…',
  },
  tr: {
    note: `Doğrulama tamamlanamadı. Lütfen sayfayı yenileyin veya ${CONTACT_FALLBACK_EMAIL} adresine yazın.`,
    verifying: 'Doğrulanıyor…',
  },
}

/**
 * The words after the number under the message box, per locale; any other
 * locale falls back to English, as `CHECK_COPY` does and for the same reason.
 * The number is a separate `<span>` so `SUBMIT_JS` can change it alone on a
 * page that never hydrates.
 */
const LEFT_COPY: Record<string, string> = {
  en: 'characters left',
  tr: 'karakter kaldı',
}

const THANKS_EN: ContactFormThanks = {
  download: [
    'Thank you for your interest! We’ve sent the download link to your email.',
    'Please check your inbox (and your spam folder, just in case) to access the materials.',
    'You can also download it directly using the button below.',
  ],
  contact: [
    'Thank you for getting in touch with us.',
    'Our team has received your message and will get back to you shortly.',
  ],
  help: {
    before: 'If you have any questions or need further assistance, ',
    link: 'contact us',
    after: ' here.',
    href: '/en/contact-us',
  },
  downloadLabel: 'Download now',
  requiredNote: 'Please choose an option.',
}

/**
 * The attribute a Download / gated CTA carries instead of linking straight at
 * the PDF: `href="#contact"` scrolls the reader to the form at the foot of the
 * page, and this remembers which file they were after so the thank-you panel can
 * hand it over. See `lib/gatedDownload.ts`.
 */
const GATED = 'data-gated-download'

/**
 * The word in the little search box at the top of a long picker. Kept here
 * rather than in the dictionary because it is one word per locale and adding a
 * key to `en.ts` would oblige every locale file to be edited to say "Search".
 * English is the fallback, as everywhere else.
 */
const SEARCH_LABEL: Record<string, string> = {
  en: 'Search',
  tr: 'Ara',
  es: 'Buscar',
  de: 'Suchen',
  fr: 'Rechercher',
  pt: 'Pesquisar',
}

/** A picker shorter than this answers faster by eye than by typing. */
const SEARCH_FROM = 8

/*
  Typing to find an option — "t, ü and Türkiye is there" (2026-08-30).

  A native `<select>` gives that for free and this picker is not one (see the
  long note beside the field for why it cannot be). So the behaviour is put back
  by hand: a search box at the top of the panel, focused the moment the list
  opens, and any letter typed while the list is open goes into it even if the
  pointer is out over the options.

  It is an **inline script**, emitted with the form exactly as `pickerCss` is,
  because 117 of the site's 154 documents ship no client bundle and a search box
  that only worked on the other 37 would be worse than none. Everything is
  delegated off `document`, so nothing depends on when the elements appear, and
  the `dp-js` class it puts on `<html>` is both the "enhanced" switch that
  reveals the boxes (they are `display:none` without it, so a reader with no
  JavaScript never sees a control that does nothing) and the guard that keeps a
  second form on the same page from wiring everything twice.

  Matching folds accents away — `normalize('NFD')` strips the umlaut off ü and
  the cedilla off ş, and dotless ı is mapped by hand because it carries no
  combining mark. So "turkiye", "türkiye" and "TÜRK" all find Türkiye, which is
  the point: nobody types the diacritics into a search box.

  It matches from the START of a word, not anywhere in the string — "t ye
  bastığımda t ile başlayan ülkeler gelsin" (2026-08-30). A plain substring
  search answered "t" with Botswana and Antarctica, which reads as broken even
  though it is not. Every separator (space, hyphen, comma, bracket) folds to a
  space and a space is prefixed to both sides, so " t" finds Türkiye, Thailand
  and Timor-Leste; "states" still finds United States, because the match is per
  word rather than only at the very beginning of the name.

  Two things it also has to do, both asked for on 2026-08-30 ("industry country
  seçtikten sonra bizi gereksiz şekilde aşağı kaydırıyor sayfa"):

  * **The page must not move when a picker opens, closes or is answered.** Every
    option is a label wrapping an `sr-only` radio, and `sr-only` is
    `position:absolute` with no offsets — so the radio sits at its static
    position, which for the two-hundredth country is a long way down an open
    panel. Clicking the label focuses it, the browser scrolls that focus into
    view, and then the panel collapses and leaves the reader parked below the
    form. So `hold()` records the field's position at `mousedown` (capture
    phase, before focus moves) and `keep()` scrolls back by whatever the browser
    changed once the answer has landed. `focus({preventScroll:true})` on the
    search box is the same problem from the other end.
  * **Clicking anywhere else closes an open panel, and that is what makes the
    form submittable** (2026-09-01: "bu sayfada form doldurunca submit
    edemiyorum"). Closing was CSS-only — answer the list, or click the control
    again — so a reader who opened Industry, found nothing they wanted and
    clicked away was left with an open panel. It is `absolute z-20` and 22.5rem
    tall, which on the closing band is exactly the height of the message box,
    the consent line, the privacy note and the Submit button: the click landed
    on the panel and the button never saw it. So the same capture-phase
    `mousedown` that records the scroll position now unchecks any `dp-open`
    outside the field being clicked. The field's own clicks are skipped, or
    choosing an option would close the list before the option registered.

  * **Hovering the open panel is enough to type into it.** The letter keys were
    already caught on `document`, but the pointer being over the list while
    focus had drifted elsewhere was the case that read as dead. Moving onto the
    panel now puts the caret in its search box, so "t" filters straight away
    without a click first.
*/
const PICKER_JS = `(function(){var D=document,H=D.documentElement;
if(H.classList.contains('dp-js'))return;H.classList.add('dp-js');
function fold(s){return(' '+s.toLowerCase().normalize('NFD').replace(/[\\u0300-\\u036f]/g,'').replace(/\\u0131/g,'i').replace(/[^a-z0-9]+/g,' ')).replace(/ +$/,'')}
function filter(p,q){var f=fold(q.trim()),l=p.querySelectorAll('.dp-option'),i,el,t;
for(i=0;i<l.length;i++){el=l[i];t=el.__dp||(el.__dp=fold(el.textContent||''));
el.classList.toggle('dp-hide',f!==''&&t.indexOf(f)<0)}p.scrollTop=0}
function box(f){return f&&f.querySelector('.dp-search')}
var AF=null,AT=0;
function hold(f){AF=f||null;if(AF)AT=AF.getBoundingClientRect().top}
function keep(){if(!AF)return;var f=AF;AF=null;setTimeout(function(){
var d=f.getBoundingClientRect().top-AT;if(d)window.scrollBy(0,d)},0)}
D.addEventListener('mousedown',function(e){var t=e.target;
var f=t&&t.closest?t.closest('.dp-field'):null;hold(f);
var o=D.querySelectorAll('.dp-field .dp-open:checked'),i;
for(i=0;i<o.length;i++){if(o[i].closest('.dp-field')!==f)o[i].checked=false}},true);
D.addEventListener('input',function(e){var s=e.target;
if(s.classList&&s.classList.contains('dp-search')){var p=s.closest('.dp-panel');if(p)filter(p,s.value)}});
D.addEventListener('change',function(e){var t=e.target;if(!t.classList)return;
if(t.classList.contains('dp-open')&&t.checked){var f=t.closest('.dp-field'),s=box(f);
if(s){s.value='';filter(f.querySelector('.dp-panel'),'');setTimeout(function(){s.focus({preventScroll:true})},0)}}
if(t.classList.contains('dp-open')||t.classList.contains('dp-choice')||t.classList.contains('dp-shut'))keep()});
D.addEventListener('mouseover',function(e){var t=e.target;if(!t||!t.closest)return;
var p=t.closest('.dp-panel');if(!p)return;var f=p.closest('.dp-field');
var r=f&&f.querySelector('.dp-open');if(!r||!r.checked)return;
var s=box(f);if(s&&D.activeElement!==s)s.focus({preventScroll:true})});
D.addEventListener('keydown',function(e){var t=e.target;
if(t.classList&&t.classList.contains('dp-search')){
if(e.key==='Enter'){e.preventDefault();var p=t.closest('.dp-panel'),
o=p&&p.querySelector('.dp-option:not(.dp-hide) input');if(o){hold(t.closest('.dp-field'));o.click()}return}
if(e.key==='Escape'){var r=t.closest('.dp-field').querySelector('.dp-open');if(r)r.checked=false;return}
return}
if(!e.key||e.key.length!==1||e.ctrlKey||e.metaKey||e.altKey)return;
var g=t.tagName;if(g==='TEXTAREA'||g==='SELECT'||(g==='INPUT'&&t.type!=='radio'))return;
var open=D.querySelector('.dp-field .dp-open:checked');if(!open)return;
var fl=open.closest('.dp-field'),s=box(fl);if(!s)return;
e.preventDefault();s.focus({preventScroll:true});s.value+=e.key;filter(fl.querySelector('.dp-panel'),s.value)});})()`

/*
  Submitting the form on a page that never hydrates — which is most of them.

  Same reasoning as `PICKER_JS` above, and the same delivery: an inline script
  emitted with the form, so it is there on all 154 documents rather than the 37
  that ship a bundle. It does what `handleSubmit` does — check the pickers, read
  the fields, put the thank-you panel up, post to `/api/form-lead` behind it —
  against markup that is already in the page, which is why the panel is rendered
  hidden below instead of being built here: the wording is per-locale and belongs
  in the dictionary, not in a string of JavaScript.

  Three things worth not undoing:

  * **`if(e.defaultPrevented)return`** is the whole coordination with React. A
    hydrated page runs `handleSubmit` first (React listens on the root, this
    listens on `document`, and the event bubbles outward), and its first line is
    `preventDefault`. So exactly one of the two handles any given submit.
  * **Turnstile is rendered here only on a page with no bundle, and not until
    the document has finished parsing.** The Worker refuses a submission with
    no token whenever `TURNSTILE_SECRET` is set, so a form on a static page
    needs the widget as much as the others — but a hydrated page gets it from
    React's effect instead, and `mount()` steps aside for that by looking for
    OUR bundle — `<script type="module" src="/assets/…">`, which is exactly the
    tag the prerender strips to make a page static. Two renderers on one div is
    the bug this whole paragraph exists because of.
    **"Our bundle", not "a module script" (2026-09-28).** It said the latter
    for a fortnight, and it held only until Cloudflare's edge began appending
    its Web Analytics beacon to every page as
    `<script type="module" src="https://static.cloudflareinsights.com/…">`.
    From then on every static page believed it would hydrate, stood aside for a
    React that was never coming, and rendered no widget — so every contact form
    on 166 pages refused to send, telling the reader to complete a check that
    was not on the screen. Our HTML was correct the whole time; the edge edited
    it after the build. `scripts/check-forms.mjs` now serves the build WITH
    that beacon added, so a check can see what a visitor sees.
  * **No token yet is not a failure.** A reader who presses Send before the
    invisible check has finished is told "verifying", the widget is rendered
    if it somehow is not, and the submit completes itself from Turnstile's
    callback — the newsletter box's pattern since 2026-09-08. Only after 8 s
    with no token does the "complete the check" line appear.
    The wait for `DOMContentLoaded` is a second, separate hazard: this script
    is emitted INSIDE the form, above the fields, so at the moment it runs the
    widget's own `div` is still further down the byte stream and
    `querySelector` answers null — which is how every contact form on the site
    came to submit with no token at all, and be refused with the reader looking
    at a thank-you.
  * The panel goes up **before** the fetch, for the reason `handleSubmit`
    explains: a failed save is a line of text, not a wall.
*/
/**
 * The two expandos `SUBMIT_JS` writes on a Turnstile div — the widget id it
 * rendered, and the reason it could not. Only the script writes them: React's
 * effect keeps its id in a ref, for the reason spelled out where it renders.
 */
type TurnstileBox = HTMLElement & { __id?: string; __err?: string }

/**
 * Form telemetry for PostHog — Fatih, 2026-09-28, so a form that stops working
 * shows up as a line on a chart rather than as a colleague noticing leads have
 * gone quiet. **Never a field value**: no name, email, company or message goes
 * in, only which form, which page, and why it could not send. `window.posthog`
 * is the site's own snippet; when it is absent or the visitor has not consented
 * (it honours its own opt-out), this does nothing. `SUBMIT_JS` carries the same
 * three events under the same names for pages with no bundle.
 */
type FormEvent =
  | { name: 'form_submitted'; props: { form_id: string; page: string; verification: 'ok' | 'failed'; locale: string } }
  | { name: 'form_blocked'; props: { form_id: string; page: string; reason: string; cf_error_code: string | null } }
  | { name: 'turnstile_error'; props: { page: string; cf_error_code: string } }
function track(e: FormEvent) {
  try {
    // `__loaded`, like `dp.track` in index.html: the same rule for whether
    // PostHog may be told anything, so consent behaves identically on both.
    const ph = (window as unknown as { posthog?: { __loaded?: boolean; capture?: (n: string, p: object) => void } })
      .posthog
    if (ph?.__loaded) ph.capture?.(e.name, e.props)
  } catch {
    /* telemetry must never be why a lead is lost */
  }
}

const SUBMIT_JS = `(function(){var D=document,H=D.documentElement;
if(H.classList.contains('dp-form-js'))return;H.classList.add('dp-form-js');
var T0=Date.now(),G=null;
function ev(n,p){try{var P=window.posthog;if(P&&P.__loaded&&P.capture)P.capture(n,p)}catch(x){}}
function fid(f){return(f.getAttribute('data-thanks')||'form').replace(/-thanks$/,'')}
D.addEventListener('click',function(e){var t=e.target;if(!t||!t.closest)return;
var a=t.closest('a[data-gated-download]');if(a)G=a.getAttribute('data-gated-download')});
D.addEventListener('input',function(e){var t=e.target;if(!t||!t.getAttribute)return;
var c=D.getElementById(t.getAttribute('data-dp-left')||'');if(!c||t.maxLength<0)return;
if(D.querySelector('script[type="module"][src^="/assets/"]'))return;
c.textContent=String(t.maxLength-t.value.length)});
function draw(el){if(el.__id||!window.turnstile)return;
try{el.__id=window.turnstile.render(el,
{sitekey:el.getAttribute('data-dp-turnstile'),appearance:'always',retry:'auto',
callback:function(){var w=el.__wait;if(w){el.__wait=null;w()}},
'expired-callback':function(){try{window.turnstile.reset(el.__id)}catch(x){}},
'error-callback':function(c){el.__err=c||'unknown';
ev('turnstile_error',{page:location.pathname,cf_error_code:el.__err});
console.error('[dp] turnstile refused to render:',c)}})}
catch(x){el.__err='render threw';console.error('[dp] turnstile render threw:',x)}}
function drawAll(){var l=D.querySelectorAll('[data-dp-turnstile]'),i;
for(i=0;i<l.length;i++)draw(l[i])}
function load(){if(window.turnstile){drawAll();return}
var s=D.querySelector('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]');
if(s){s.addEventListener('load',drawAll);return}
s=D.createElement('script');
s.src='https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
s.async=true;s.defer=true;s.onload=drawAll;
s.onerror=function(){s.setAttribute('data-dp-failed','1')};D.head.appendChild(s)}
function mount(){if(!D.querySelector('[data-dp-turnstile]'))return;
if(D.querySelector('script[type="module"][src^="/assets/"]'))return;
load()}
function token(W){if(!W||!W.__id||!window.turnstile)return undefined;
try{return window.turnstile.getResponse(W.__id)||undefined}catch(x){return undefined}}
if(D.readyState==='loading'){D.addEventListener('DOMContentLoaded',mount)}else{mount()}
function show(el){if(el)el.classList.remove('dp-hide')}
function hide(el){if(el)el.classList.add('dp-hide')}
D.addEventListener('submit',function(e){
if(e.defaultPrevented)return;var f=e.target;
if(!f.classList||!f.classList.contains('dp-form'))return;
e.preventDefault();
var req=f.querySelectorAll('.dp-field[data-required]'),i,miss=false;
for(i=0;i<req.length;i++){if(req[i].querySelector('.dp-choice:checked')){
hide(req[i].querySelector('.dp-required'))}else{show(req[i].querySelector('.dp-required'));miss=true}}
if(miss)return;
var a={};new FormData(f).forEach(function(v,k){
if(typeof v!=='string'||!v.trim()||k==='website')return;
a[k]=a[k]?a[k]+', '+v.trim():v.trim()});
var own={firstname:1,lastname:1,email:1,company:1,phone:1,message:1,
'cf-turnstile-response':1},rest={},k;
for(k in a)if(!own[k])rest[k]=a[k];
var n=[a.firstname,a.lastname].filter(Boolean).join(' ').trim();
var p=D.getElementById(f.getAttribute('data-thanks'));
var W=f.querySelector('[data-dp-turnstile]');
if(W&&!token(W)){if(f.__waiting)return;f.__waiting=true;
var cn=f.querySelector('.dp-check-needed'),vf=f.querySelector('.dp-verifying'),
bt=f.querySelector('button[type=submit]');hide(cn);show(vf);if(bt)bt.disabled=true;
if(!W.__id)load();
var tm,done=function(){clearTimeout(tm);W.__wait=null;f.__waiting=false;hide(vf);
if(bt)bt.disabled=false;var t=token(W);if(t){send(t);return}
console.warn('[dp] no turnstile token after waiting; sending unverified',W.__err||'');send(null)};
W.__wait=done;tm=setTimeout(done,8000);return}
send(token(W));
function send(tk){
if(p){show(p);if(G){show(p.querySelector('.dp-thanks-download'));
hide(p.querySelector('.dp-thanks-contact'));
var b=p.querySelector('.dp-thanks-file');if(b){b.href=G;show(b)}}}
hide(f);hide(f.parentNode.querySelector('.dp-form-note'));
if(p&&p.scrollIntoView)p.scrollIntoView({block:'center'});
fetch('/api/form-lead',{method:'POST',headers:{'content-type':'application/json'},
body:JSON.stringify({source_page:location.pathname,source_title:D.title.slice(0,300),
intent:G?'download':'contact',asked_for:G||null,locale:H.lang||'en',
full_name:n||a.email||'',email:a.email||'',company:a.company||null,
phone:a.phone||null,message:a.message||null,answers:rest,consent:true,
elapsed_ms:Date.now()-T0,website:'',turnstile:tk})})
.then(function(r){if(r.ok){ev('form_submitted',{form_id:fid(f),page:location.pathname,
verification:tk?'ok':'failed',locale:H.lang||'en'});return}
ev('form_blocked',{form_id:fid(f),page:location.pathname,reason:'api_'+r.status,cf_error_code:null});
if(p)show(p.querySelector('.dp-thanks-failed'))})
.catch(function(){ev('form_blocked',{form_id:fid(f),page:location.pathname,reason:'network',cf_error_code:null});
if(p)show(p.querySelector('.dp-thanks-failed'))})}});})()`

export default function ContactForm({
  fields,
  consent,
  submit,
  confirmation,
  privacy,
  thanks = THANKS_EN,
  /** Distinguishes the two `id`s when both instances land on one document. */
  idPrefix = '',
  /**
   * `grid` pairs the fields two to a row, which is what the Contact Us mockup
   * asks for (2026-08-26) and what a form beside a headline needs — nine fields
   * in one column is a page and a half of scrolling. The home page's block sits
   * in a narrower centred column and stays `stacked`.
   */
  layout = 'stacked',
}: ContactFormContent & { idPrefix?: string; layout?: 'stacked' | 'grid' }) {
  const noteId = `${idPrefix}form-pending`
  const thanksId = `${idPrefix}form-thanks`
  /** The same id `SUBMIT_JS` derives from `data-thanks`, so both paths report one name. */
  const formId = `${idPrefix}form`
  // Half-width fields at `text-lg` read as oversized; full-width ones do not.
  const control = `mt-2 w-full rounded-md border border-border bg-card px-4 py-3 ${
    layout === 'grid' ? 'text-base' : 'text-lg'
  }`

  /*
    The pickers' open/closed state and their chosen value are both plain CSS,
    generated here as one rule per option and emitted with the form. It has to
    be CSS: 117 of the site's 154 documents ship no client bundle, so on most
    pages the React `onChange` below never runs — which is exactly what the
    2026-08-30 capture showed, a list that stayed open with a bare tick in the
    closed control instead of the country somebody had just clicked.

    Two ids do the work per field: `<base>` is the "open" radio, and it sits in
    the SAME radio group as the options. That single fact is what closes the
    panel with no JavaScript — checking an option unchecks the open radio,
    because a radio group holds one answer, and the panel is only displayed
    while the open radio is checked. Picking again means reopening, which
    unchecks the answer, which is honest: what the control shows is always the
    radio the form would submit.

    The rules are per-option and there are ~250 countries, so this is a few KB
    of near-identical text in the HTML — it gzips to almost nothing, and it is
    the price of the pickers working on a page with no JavaScript at all.
  */
  /*
    Three rules that belong to the search box rather than to any one field:
    `dp-hide` is what the filter uses to drop a non-matching option (it needs
    `!important` because the option is a `flex` row, which outranks a plain
    `display:none`), and the search box itself stays hidden until the inline
    script announces itself with `dp-js` on `<html>` — a text box that filtered
    nothing on the 117 pages with no bundle would be a trap.
  */
  const searchCss = `.dp-hide{display:none!important}.dp-search{display:none}.dp-js .dp-search{display:block}`

  const pickerCss = fields
    .filter((f) => f.type === 'select')
    .map((f) => {
      const base = `${idPrefix}${f.name}`
      const sel = `#${base}-picker`
      const options = [...(f.options ?? []), ...(f.other ? [f.other.label] : [])]
      return [
        `${sel} .dp-panel{display:none}`,
        `${sel}:has(#${base}:checked) .dp-panel{display:block}`,
        `${sel} .dp-echo{display:none}`,
        `${sel}:has(.dp-choice:checked) .dp-placeholder{display:none}`,
        /*
          Clicking the control a second time closes the list again, without
          answering it (2026-08-30: "select kısmına tıklayınca seçim yapmadan
          geri kapansınlar"). A checked radio cannot be unchecked by clicking
          it, so while the panel is open the closed control is swapped for a
          second one that looks the same and holds a THIRD radio in the same
          group — checking it unchecks the open radio, the panel goes away and
          the field is still unanswered, because that radio's value is empty.
          CSS again, so it works on the 117 documents with no bundle.
        */
        `${sel} .dp-closer{display:none}`,
        `${sel}:has(#${base}:checked) .dp-lead{display:none}`,
        `${sel}:has(#${base}:checked) .dp-closer{display:flex}`,
        ...options.map((_, i) => `${sel}:has(#${base}-o${i}:checked) #${base}-e${i}{display:block}`),
      ].join('')
    })
    .join('')

  const { locale } = useI18n()
  const [picked, setPicked] = useState<Record<string, string>>({})
  /** Which boxes are ticked, per `checkboxes` field. */
  const [ticked, setTicked] = useState<Record<string, string[]>>({})
  const [missing, setMissing] = useState<string[]>([])
  /** How long the message box's answer is, for the count under it. */
  const [written, setWritten] = useState(0)
  const [sent, setSent] = useState<null | { download?: string }>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  /**
   * Turnstile produced no token, so nothing was sent.
   *
   * Deliberately NOT folded into `saveFailed`: that one means "we have your
   * message and could not file it", shown behind the thank-you panel, and this
   * one means "we do not have your message" and has to be shown beside the
   * form the reader is still looking at. Collapsing them would tell somebody
   * their message arrived when it did not.
   */
  const [checkFailed, setCheckFailed] = useState(false)
  /** A submit parked until Turnstile's callback delivers a token — see `handleSubmit`. */
  const [verifying, setVerifying] = useState(false)
  const pendingSubmit = useRef<(() => void) | null>(null)
  const widgetBox = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | null>(null)

  /*
    ON A HYDRATED PAGE, REACT OWNS THE WIDGET. Proven necessary on 2026-09-02,
    from the browser console on the live site:

      Error: Minified React error #418            (hydration mismatch)
      [Cloudflare Turnstile] Cannot find Widget cf-chl-widget-q2100
      document.querySelector('[data-dp-turnstile]').__id  →  undefined

    `SUBMIT_JS` renders the widget at DOMContentLoaded, into the div that came
    from the server. Then React hydrates, finds children it did not render,
    and REPLACES THE DIV. The new element carries none of the script's state,
    and Turnstile's own registry now points at a node that is gone — so
    `getResponse` throws and there is no token, on a form that looks perfectly
    normal.

    It is a RACE, which is why it was so confusing to chase: whichever finishes
    last wins. Kagan saw the widget appear and solved it at 13:13, and saw
    nothing at all at 15:39, on the same page and the same build.

    So this effect renders the widget itself, after hydration, where React
    cannot then throw it away. `SUBMIT_JS` is left alone for pages that ship no
    bundle at all — it is the only thing that works there.

    THE ORPHAN IS GONE TOO (2026-09-14). For a fortnight the script still
    rendered its own widget first on a hydrated page, and React then replaced
    the div out from under it: inert, one wasted challenge fetch per page load,
    and the `Cannot find Widget` line that made the original diagnosis so hard
    to read. `mount()` now bails when the document carries a
    `<script type="module">`.

    **That tag is the signal because it is the same fact, read off the
    artifact.** `scripts/prerender.mjs` builds a static page by STRIPPING that
    script — it is literally what "this page will not hydrate" means here — so
    the script cannot disagree with the build the way a prop threaded down from
    the prerender could. It also fails in the safe direction: if the strip ever
    breaks, the module tag is present, the script defers, and React (which will
    now hydrate) renders the widget.
  */
  useEffect(() => {
    if (!TURNSTILE_ENABLED) return
    let cancelled = false
    loadTurnstile()
      .then(() => {
        const el = widgetBox.current
        if (cancelled || !el || !window.turnstile || widgetId.current) return
        // Anything the script left inside this div belongs to a widget React
        // has already detached; clearing it stops two challenges stacking.
        el.replaceChildren()
        // NOT stamped on the element as `__id`, though the obvious symmetry
        // says it should be. `SUBMIT_JS`'s submit path reads `W.__id` as its
        // fallback, but that path cannot run on a page where this effect has
        // run: React attaches `handleSubmit` during hydration, before any
        // effect, and its first line is `preventDefault` — which is exactly
        // what `SUBMIT_JS` checks. If the bundle never executes, neither does
        // this. The line was written, found to be covered by no test, and then
        // found to be reachable by no visitor; it is recorded here so it is
        // not added back as an obvious omission.
        widgetId.current = window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          // RETRY IS SAID OUT LOUD (2026-10-02). 'auto' is Turnstile's default,
          // so this changes nothing today; it is written so a later edit cannot
          // turn it off unnoticed — `verify-turnstile.mjs` requires it at every
          // render. Do NOT add a `reset()` to the error-callback as well: the
          // widget is already retrying, and a reset would restart it mid-retry.
          retry: 'auto',
          // 'always', not 'interaction-only' (Fatih, 2026-09-28): when Cloudflare
          // decides a reader must click, an invisible-until-needed widget in a
          // container that collapses when empty is the one state in which the
          // reader cannot find what they are being asked to do — and it is the
          // state phones land in most. A visible box costs 65px.
          appearance: 'always',
          // Clears the "complete the check" line the moment the check passes,
          // so somebody who submitted early and then solved it is not still
          // being told to do something they have done.
          callback: () => {
            setCheckFailed(false)
            const resume = pendingSubmit.current
            if (resume) {
              pendingSubmit.current = null
              resume()
            }
          },
          'expired-callback': () => {
            try {
              if (widgetId.current) window.turnstile?.reset(widgetId.current)
            } catch {
              /* the next submit waits for a fresh token either way */
            }
          },
          'error-callback': (code?: string) => {
            track({ name: 'turnstile_error', props: { page: location.pathname, cf_error_code: code || 'unknown' } })
            console.error('[dp] turnstile refused to render:', code)
          },
        })
      })
      .catch((e) => console.error('[dp] turnstile failed to load:', e))
    return () => {
      cancelled = true
    }
  }, [])
  const pendingDownload = useRef<string | undefined>(undefined)
  const thanksRef = useRef<HTMLDivElement>(null)
  const openedAt = useRef(Date.now()).current

  /*
    Turnstile is the same widget and the same settings the tools' `LeadGate`
    uses. Exactly one thing renders it per document: the effect above where the
    page hydrates, `SUBMIT_JS` where it does not. It has to exist at all because
    the Worker refuses a submission with no token whenever `TURNSTILE_SECRET` is
    set, and it is set. Invisible until it needs to challenge, so most visitors
    see nothing.

    A blocked or failed third-party script must not brick the form: nothing here
    waits on it, and the Worker decides what a missing token means.
  */

  /*
    Which file the reader asked for, if any. The Download buttons on the e-book
    and report pages scroll here instead of opening the PDF, so by the time the
    form is submitted the click that started it has already happened somewhere
    else on the page. A single delegated listener is why no section renderer has
    to know this component exists.
  */
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      const link = target?.closest?.(`a[${GATED}]`)
      if (link) pendingDownload.current = link.getAttribute(GATED) ?? undefined
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  /*
    Where the reader is looking once the panel replaces the fields.

    The form is tall — nine fields, and the pickers make it taller — so somebody
    who has just pressed Send is usually scrolled well down it. Swapping it for
    a short thank-you panel shrinks the document under them, and the browser
    keeps the scroll offset it had: the page lands at its own foot with the
    panel somewhere above, which reads as "the form threw me to the bottom"
    (2026-08-31). Scrolling the panel into view is the answer, and it has to
    happen in both submit paths — the inline `SUBMIT_JS` does the same thing
    after it hides the fields.

    `block: 'center'` rather than `'start'`: the panel is a few lines and the
    header is sticky, so aligning its top puts it under the bar on a phone. No
    `behavior: 'smooth'` — an animation nobody asked for, on a page that has
    just changed under them.
  */
  useEffect(() => {
    if (sent) thanksRef.current?.scrollIntoView({ block: 'center' })
  }, [sent])

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    /*
      The text fields validate natively (`required` below). The pickers cannot:
      a required radio inside a closed `<details>` is a control the browser
      refuses to focus, so it blocks the submit with nothing on screen. They are
      checked here instead, and named in `missing` so the field says so.
    */
    const unanswered = fields
      .filter(
        (f) =>
          f.required
          && ((f.type === 'select' && !picked[f.name])
            // A required checkbox group asks for at least one box; the browser
            // would ask for *that* box, which is not the same question.
            || (f.type === 'checkboxes' && !(ticked[f.name] ?? []).length)),
      )
      .map((f) => f.name)
    setMissing(unanswered)
    if (unanswered.length > 0) return

    const download = pendingDownload.current
    const data = new FormData(e.currentTarget)
    // Read BEFORE the panel replaces the fields: `setSent` unmounts the form,
    // and a FormData built after that is empty.
    const answers: Record<string, string> = {}
    for (const [k, v] of data.entries()) {
      if (typeof v !== 'string' || !v.trim() || k === 'website') continue
      // A checkbox group submits one entry per ticked box under one name, so
      // the repeats are joined rather than overwriting each other — the CRM
      // gets "Consulting Partners, Solution Partners", not the last one.
      const prev = answers[k]
      answers[k] = prev ? `${prev}, ${v.trim()}` : v.trim()
    }
    // This form's OWN widget, never "the" widget: two forms land on one
    // document (a page's own block plus the closing band) and `getResponse()`
    // with no argument answers for whichever was rendered first.
    //
    // `widgetId` is the one this component rendered in the effect above and is
    // the answer on any page that hydrates. The `__id` fallback is the script's
    // widget, which is what exists on a page with no bundle — where this
    // handler never runs, so it is belt and braces rather than a live path.
    // `__err` is the script's `error-callback` reason.
    const widget = e.currentTarget.querySelector(
      '[data-dp-turnstile]',
    ) as TurnstileBox | null
    const readToken = (): string | undefined => {
      const id = widgetId.current ?? widget?.__id
      if (!TURNSTILE_ENABLED || !id) return undefined
      // getResponse THROWS for a widget Turnstile no longer knows about, which
      // is precisely the state the hydration race used to leave behind. An
      // exception here would take the whole submit down and tell the reader
      // nothing.
      try {
        return window.turnstile?.getResponse(id) || undefined
      } catch (e2) {
        console.error('[dp] turnstile getResponse threw:', e2)
        return undefined
      }
    }
    const token = readToken()

    /*
      The panel goes up FIRST, and the save happens behind it.

      Same rule the tools follow ("the report is shown either way — a failed save
      is a line of text, not a wall"), and it matters more here: this person has
      asked to be called, or asked for a file we promised to show them. Making
      them watch a spinner, or telling them our CRM was briefly unreachable,
      helps nobody and loses the download they came for. If the save does fail
      they are told, quietly, with an address that always works.
    */
    // NO TOKEN YET IS NOT A FAILURE (2026-09-28). The check is invisible and
    // usually solves in about a second, so a reader who presses Send quickly
    // is told "verifying" and the submit finishes itself from the widget's
    // callback. After 8 s with no token the form is SENT WITHOUT ONE
    // (2026-10-01): the Worker accepts it as unverified rather than refusing,
    // because a refused real person is a lost lead and a spam row is a task
    // somebody closes.
    const send = (t: string | undefined) => {
      setCheckFailed(false)
      setSent({ download })
      void postLead(answers, download, t).then((ok) => {
        setSaveFailed(!ok)
        track(
          ok
            ? {
                name: 'form_submitted',
                props: { form_id: formId, page: location.pathname, verification: t ? 'ok' : 'failed', locale },
              }
            : { name: 'form_blocked', props: { form_id: formId, page: location.pathname, reason: 'save_failed', cf_error_code: null } },
        )
      })
    }
    if (TURNSTILE_ENABLED && widget && !token) {
      if (pendingSubmit.current) return
      setCheckFailed(false)
      setVerifying(true)
      let timer: ReturnType<typeof setTimeout> | undefined
      const finish = () => {
        clearTimeout(timer)
        pendingSubmit.current = null
        setVerifying(false)
        const t = readToken()
        if (t) return send(t)
        // Sent anyway, as UNVERIFIED (Kagan, 2026-10-01): a check that fails for
        // a real person — Turnstile 300030/300031 on Android, Fatih's PostHog
        // of 2026-09-30 — lost the lead. The Worker records it and the task
        // says so; the honeypot and the time floor still apply.
        console.warn('[dp] no turnstile token after waiting; sending unverified', widget.__err ?? '')
        send(undefined)
      }
      pendingSubmit.current = finish
      timer = setTimeout(finish, 8000)
      return
    }
    send(token)
  }

  /**
   * Into the CRM, through the Worker on this origin.
   *
   * `source_page` is `location.pathname` — the page this form was filled on,
   * which is the whole point of the exercise (Fatih, 2026-08-30: *"biz formu
   * dolduran kişi hangi sayfadaki formu doldurmuş bilelim"*). The path rather
   * than a route id, because this form also sits at the foot of the 52 imported
   * pages, and those have no entry in the route registry to name.
   */
  async function postLead(
    answers: Record<string, string>,
    download: string | undefined,
    turnstile: string | undefined,
  ): Promise<boolean> {
    const name = [answers.firstname, answers.lastname].filter(Boolean).join(' ').trim()
    // Sent as `answers`, not as columns: job title, country and industry are
    // this form's own questions, and the next field somebody adds should reach
    // the CRM without a migration.
    const rest = { ...answers }
    // What already has a column of its own is not one of the form's questions.
    //
    // `message` was missing from this list, so it arrived twice — once as its
    // own column and once inside the bag — which nobody saw until the bag went
    // on screen on 2026-09-08. `cf-turnstile-response` is the hidden input
    // Turnstile injects into the form; `new FormData(form)` sweeps up every
    // named field, so a spent token was being stored as an answer on 12 of 31
    // rows (stripped from those in migration 20260908110000).
    for (const k of [
      'firstname',
      'lastname',
      'email',
      'company',
      'phone',
      'message',
      'cf-turnstile-response',
    ]) {
      delete rest[k]
    }

    try {
      const res = await fetch('/api/form-lead', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          source_page: location.pathname,
          source_title: document.title.slice(0, 300),
          intent: download ? 'download' : 'contact',
          asked_for: download ?? null,
          locale,
          full_name: name || answers.email || '',
          email: answers.email ?? '',
          company: answers.company ?? null,
          phone: answers.phone ?? null,
          message: answers.message ?? null,
          answers: rest,
          consent: true,
          elapsed_ms: Date.now() - openedAt,
          website: answers.website ?? '',
          turnstile,
        }),
      })
      return res.ok
    } catch {
      return false
    }
  }

  if (sent) {
    const lines = sent.download ? thanks.download : thanks.contact
    return (
      <div ref={thanksRef} className="rounded-lg border border-border bg-card/70 p-6 shadow-sm sm:p-8">
        <div role="status" className="space-y-4">
          {lines.map((line) => (
            <p key={line} className="text-lg leading-relaxed">
              {line}
            </p>
          ))}
          <p className="text-base leading-relaxed text-muted-foreground">
            {thanks.help.before}
            <a href={thanks.help.href} className="font-medium text-primary hover:underline">
              {thanks.help.link}
            </a>
            {thanks.help.after}
          </p>
          {/*
            The mail workflow does not exist yet, and the request said so: "biz
            mailine de atacağız ama buraya tıklayarak açabilirsin diye Download
            butonunu … koyabiliriz şimdilik". So the file is one click away here
            as well as in the email, and nobody who filled the form in leaves
            without it.
          */}
          {/*
            Only ever seen when the save actually failed — the panel above went
            up regardless. An address rather than "try again", because their
            answers are gone by now and retyping them is a worse ask than an
            email.
          */}
          {saveFailed && (
            <p className="text-base leading-relaxed text-muted-foreground">
              We could not record that automatically. If you do not hear from us,
              please write to{' '}
              <a
                href={`mailto:${CONTACT_FALLBACK_EMAIL}`}
                className="font-medium text-primary hover:underline"
              >
                {CONTACT_FALLBACK_EMAIL}
              </a>
              .
            </p>
          )}
          {sent.download && (
            <p className="pt-2">
              <a
                href={sent.download}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                {thanks.downloadLabel}
              </a>
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    /*
      A frame around the fields, asked for on 2026-08-26 ("formu da çerçeveleyen
      hafif opak bir kutucuk"). Here rather than on either page, because every
      place that renders this component gets it and a box added to one of them
      would be a box the others never got.
    */
    <div className="rounded-lg border border-border bg-card/70 p-6 shadow-sm sm:p-8">
      <form
        className="dp-form space-y-5"
        aria-describedby={noteId}
        data-thanks={thanksId}
        onSubmit={handleSubmit}
        /* The no-JavaScript fallback: on a page that never hydrates, a
           `method="dialog"` form outside a dialog simply does not submit, which
           is far better than reloading the page with the answers in the URL. */
        method="dialog"
      >
        {/* `searchCss` carries `dp-hide`, which the submit script uses to swap
            the fields for the thank-you panel — so it ships even on a form with
            no pickers at all. */}
        <style dangerouslySetInnerHTML={{ __html: searchCss + pickerCss }} />
        {pickerCss && <script dangerouslySetInnerHTML={{ __html: PICKER_JS }} />}
        <script dangerouslySetInnerHTML={{ __html: SUBMIT_JS }} />
        {/*
          The honeypot. A real form leaves it empty; a naive bot fills every
          field it finds. Off-screen rather than `display:none`, because some
          bots skip hidden inputs, and `tabIndex={-1}` plus `aria-hidden` keep
          it away from anybody using a keyboard or a screen reader. The Worker
          answers 200 to a filled one, so a bot never learns it was caught.
        */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          aria-hidden
          autoComplete="off"
          className="absolute left-[-9999px] h-px w-px opacity-0"
        />
        <div className={layout === 'grid' ? 'grid gap-5 sm:grid-cols-2' : 'space-y-5'}>
          {fields.map((f) => (
            /* A textarea takes the whole row in either layout: a message box
               half the width of the form is a box nobody writes a message in. */
            <div
              key={f.name}
              className={
                f.type === 'textarea' || f.type === 'checkboxes' ? 'sm:col-span-2' : undefined
              }
            >
              {/* A group of boxes has no single control to point `htmlFor` at,
                  so its question is a plain line of text above them and each
                  box carries its own label. */}
              {f.type === 'checkboxes' ? (
                <span className="block text-sm font-medium">
                  {f.label}
                  {f.required && <span className="text-primary"> *</span>}
                </span>
              ) : (
                <label htmlFor={`${idPrefix}${f.name}`} className="block text-sm font-medium">
                  {f.label}
                  {f.required && <span className="text-primary"> *</span>}
                </label>
              )}
              {f.type === 'checkboxes' ? (
                /*
                  More than one answer, so: real checkboxes, one name, one entry
                  per ticked box. `dp-field` + `data-required` + `dp-choice` are
                  the same three hooks the pickers use, which is what lets
                  `SUBMIT_JS` enforce "at least one" on the 117 documents that
                  ship no bundle, with no code of its own.
                */
                <div
                  className="dp-field mt-2 grid gap-2 sm:grid-cols-2"
                  data-required={f.required ? '' : undefined}
                >
                  {(f.options ?? []).map((o, i) => (
                    <label
                      key={o}
                      className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-card px-4 py-3 text-base transition-colors hover:bg-accent hover:text-accent-foreground has-[:checked]:border-primary has-[:checked]:bg-accent has-[:checked]:font-medium has-[:checked]:text-accent-foreground"
                    >
                      <input
                        type="checkbox"
                        id={`${idPrefix}${f.name}-c${i}`}
                        name={f.name}
                        value={o}
                        className="dp-choice mt-1"
                        onChange={(e) => {
                          const on = e.currentTarget.checked
                          setTicked((t) => {
                            const was = t[f.name] ?? []
                            return {
                              ...t,
                              [f.name]: on ? [...was, o] : was.filter((v) => v !== o),
                            }
                          })
                          if (on) setMissing((m) => m.filter((n) => n !== f.name))
                        }}
                      />
                      <span>{o}</span>
                    </label>
                  ))}
                  {/* Same construction as the pickers': always rendered, hidden
                      by `dp-hide`, so the inline script has something to reveal
                      on a page React never touches. */}
                  <p
                    className={`dp-required text-sm text-primary sm:col-span-2${
                      missing.includes(f.name) ? '' : ' dp-hide'
                    }`}
                  >
                    {thanks.requiredNote}
                  </p>
                </div>
              ) : f.type === 'textarea' && f.name === 'message' ? (
                /*
                  The message box carries the Worker's own limit, and says how
                  much is left (request 65c9f47b, 2026-10-25). Before this the
                  box had no limit and the Worker dropped a message over 4,000
                  characters whole, so the visitor's only sign was a reply that
                  never came. Only `message`: it is the one textarea with a
                  column and a limit of its own — `goal` travels in `answers`.

                  The count is written twice, like everything else here: by
                  `onInput` where the page hydrates, and by `SUBMIT_JS`'s input
                  listener where it does not. The script stands aside on a page
                  that will hydrate, because a text node it replaced is one React
                  would go on updating after it had left the document.
                */
                <>
                  <textarea
                    id={`${idPrefix}${f.name}`}
                    name={f.name}
                    rows={4}
                    required={f.required}
                    maxLength={FORM_MESSAGE_MAX}
                    aria-describedby={`${idPrefix}${f.name}-left-line`}
                    data-dp-left={`${idPrefix}${f.name}-left`}
                    onInput={(e) => setWritten(e.currentTarget.value.length)}
                    className={control}
                  />
                  <p
                    id={`${idPrefix}${f.name}-left-line`}
                    className="mt-1 text-right text-xs text-muted-foreground"
                  >
                    <span id={`${idPrefix}${f.name}-left`}>{FORM_MESSAGE_MAX - written}</span>{' '}
                    {LEFT_COPY[locale] ?? LEFT_COPY.en}
                  </p>
                </>
              ) : f.type === 'textarea' ? (
                <textarea
                  id={`${idPrefix}${f.name}`}
                  name={f.name}
                  rows={4}
                  required={f.required}
                  className={control}
                />
              ) : f.type === 'select' ? (
                /*
                  A radio listbox rather than a native `<select>`.

                  Asked for on 2026-08-30: "industry ve country'e bastığımızda
                  çıkan seçenekler tüm sayfayı kaplamasın, 10 seçenek görünsün
                  kaydırmalı olsun". A native select's popup is the browser's —
                  250 countries open as a panel over most of the page and no CSS
                  of ours can reach its height, which is what the previous
                  attempt (capping the control's width and the option font size)
                  ran into. This panel is ours: `max-h-[22.5rem]` is ten rows at
                  `py-2 text-sm`, and everything past the tenth scrolls inside it.

                  It needs no JavaScript to work, and that now covers answering
                  it as well as opening it (2026-08-30: "tıkladığımızda otomatik
                  kutucuğa o seçenek gelsin doldurulsun ve ilgili kutucuk
                  kapansın", against a capture of two lists left open with a
                  bare tick where the country should be). Opening, echoing the
                  answer and closing again are all `pickerCss` above; React only
                  keeps `picked` in step so the required check can fire.

                  **The panel floats over the page, and the field keeps its
                  closed height** — see the note on the panel itself. It sat in
                  the flow for a day (2026-08-30: "country ve industry seçince
                  garip oluyor böyle kalıyor", a capture of both lists left open
                  across the consent text), which was really a complaint about a
                  list that never closed; closing is CSS now, on every page with
                  or without a bundle, so the accordion behaviour it bought was
                  costing a reflow for nothing.
                */
                <div
                  className="dp-field group relative"
                  id={`${idPrefix}${f.name}-picker`}
                  data-required={f.required ? '' : undefined}
                >
                  {/* The closed control. Clicking it checks the "open" radio,
                      which both shows the panel and — being in the same group
                      as the options — clears whatever was chosen, so reopening
                      the list always asks the question again rather than
                      showing one answer while holding another. */}
                  <label
                    className={`dp-lead mt-2 flex max-w-xs cursor-pointer items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 ${
                      layout === 'grid' ? 'text-base' : 'text-lg'
                    }`}
                  >
                    <input
                      type="radio"
                      id={`${idPrefix}${f.name}`}
                      name={f.name}
                      value=""
                      className="dp-open sr-only"
                      onChange={() =>
                        setPicked((p) => {
                          const { [f.name]: _cleared, ...rest } = p
                          return rest
                        })
                      }
                    />
                    <span className="dp-placeholder truncate text-muted-foreground">
                      {f.placeholder || ''}
                    </span>
                    {/* One span per option, all hidden until its own radio is
                        checked. The chosen answer is therefore real text in the
                        control on every page, bundle or no bundle. */}
                    {[...(f.options ?? []), ...(f.other ? [f.other.label] : [])].map((o, i) => (
                      <span key={o} id={`${idPrefix}${f.name}-e${i}`} className="dp-echo truncate">
                        {o}
                      </span>
                    ))}
                    <span aria-hidden className="ml-auto text-xs">
                      ▾
                    </span>
                  </label>
                  {/* The same control again, shown only while the list is open
                      and hidden the rest of the time — clicking it checks a
                      radio whose value is empty, which closes the panel and
                      leaves the field unanswered. It carries the placeholder
                      rather than an echo because opening the list has already
                      cleared whatever was chosen. */}
                  <label
                    className={`dp-closer mt-2 max-w-xs cursor-pointer items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 ${
                      layout === 'grid' ? 'text-base' : 'text-lg'
                    }`}
                  >
                    <input
                      type="radio"
                      id={`${idPrefix}${f.name}-shut`}
                      name={f.name}
                      value=""
                      className="dp-shut sr-only"
                      aria-label={f.placeholder || f.label}
                      onChange={() =>
                        setPicked((p) => {
                          const { [f.name]: _cleared, ...rest } = p
                          return rest
                        })
                      }
                    />
                    <span className="truncate text-muted-foreground">{f.placeholder || ''}</span>
                    <span aria-hidden className="ml-auto text-xs">
                      ▴
                    </span>
                  </label>
                  {/* `overscroll-contain`: reaching the end of the list with the
                      wheel stops there instead of carrying on down the page,
                      which is the other half of "gereksiz şekilde aşağı
                      kaydırıyor" (2026-08-30). */}
                  {/* It floats over what is below it rather than sitting in the
                      flow (2026-08-31: "seçeneklere tıklayınca kutunun genişliği
                      değişiyor … message kutusu aşağıya indi"). An accordion
                      that pushes the rest of the form down moves the field the
                      reader is aiming at, which is worse than covering the two
                      rows underneath — and the reason the panel was put in the
                      flow in the first place (a list left open on a page with no
                      bundle) no longer applies: `pickerCss` closes it on the
                      answer, and `dp-closer` closes it without one, both in CSS
                      alone. `absolute` also means the field keeps its closed
                      height, so nothing around it reflows. */}
                  <div className="dp-panel absolute left-0 top-full z-20 mt-1 max-h-[22.5rem] w-full max-w-xs overflow-y-auto overscroll-contain rounded-md border border-border bg-card pb-1 shadow-lg">
                    {/* Sticky, so it is still there after scrolling to the B's.
                        No `name`, so it is not one of the form's answers, and
                        the script swallows Enter inside it — otherwise the
                        first letter typed into a search box would submit the
                        form.

                        It must have no transparent band above or below it, or
                        the rows scrolling past show through and the box looks
                        half-erased (2026-08-30: "search'ün üstünde kalan kısım
                        hafif gözüküyor ve hataymış gibi oluyor"). Two of them
                        existed: a sticky box stops at the scrollport's padding
                        edge, so the panel's own top padding was a strip the
                        list scrolled through — hence `pb-1` rather than `py-1`
                        above — and the box's `mb-1` was a second strip under
                        it. The gap to the first row is the row's own `py-2`. */}
                    {[...(f.options ?? []), ...(f.other ? [f.other.label] : [])].length >=
                      SEARCH_FROM && (
                      <input
                        type="text"
                        className="dp-search sticky top-0 z-10 w-full border-b border-border bg-card px-4 py-2 text-sm outline-none"
                        placeholder={SEARCH_LABEL[locale] ?? SEARCH_LABEL.en}
                        aria-label={SEARCH_LABEL[locale] ?? SEARCH_LABEL.en}
                        autoComplete="off"
                      />
                    )}
                    {[...(f.options ?? []), ...(f.other ? [f.other.label] : [])].map(
                      (o, i, all) => {
                        const value = f.other && i === all.length - 1 ? 'other' : o
                        return (
                          <label
                            key={o}
                            /* `bg-sidebar-accent` was the hover here and it
                               painted nothing: this app's tailwind.config.ts
                               deliberately carries no sidebar scale, so the
                               class generated no rule and a row under the
                               pointer looked identical to every other row —
                               "seçeneklerin bir hissiyatı yok" (2026-08-30).
                               `accent` is the token that exists, and the same
                               tint marks the chosen row. */
                            className="dp-option flex cursor-pointer items-center px-4 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[:checked]:bg-accent has-[:checked]:font-medium has-[:checked]:text-accent-foreground"
                          >
                            <input
                              type="radio"
                              id={`${idPrefix}${f.name}-o${i}`}
                              name={f.name}
                              value={value}
                              className="dp-choice sr-only"
                              onChange={() => {
                                setPicked((p) => ({ ...p, [f.name]: o }))
                                setMissing((m) => m.filter((n) => n !== f.name))
                              }}
                            />
                            {o}
                          </label>
                        )
                      },
                    )}
                  </div>
                  {/*
                    The box you type your own answer into, revealed by picking
                    "Other" (asked for on 2026-08-26). Seventeen industries is the
                    list we publish reports against, not the list of every
                    business there is, so the eighteenth enquiry needs somewhere
                    to go other than back out of the form.

                    CSS, not JavaScript, so it opens on the pages with no bundle
                    too. The value is spelled out in the class rather than
                    interpolated: Tailwind reads these files as text, so a class
                    it cannot see whole is a rule it never generates.
                  */}
                  {f.other && (
                    <input
                      type="text"
                      id={`${idPrefix}${f.name}-other`}
                      name={`${f.name}_other`}
                      placeholder={f.other.placeholder}
                      aria-label={f.other.placeholder ?? f.other.label}
                      className={`${control} hidden max-w-xs group-has-[input[value=other]:checked]:block`}
                    />
                  )}
                  {/* Always in the page, hidden by `dp-hide`: React toggles the
                      class where it hydrates, the inline script where it does
                      not. A note rendered conditionally would not exist for the
                      script to reveal. */}
                  <p
                    className={`dp-required mt-2 text-sm text-primary${
                      missing.includes(f.name) ? '' : ' dp-hide'
                    }`}
                  >
                    {thanks.requiredNote}
                  </p>
                </div>
              ) : (
                <input
                  id={`${idPrefix}${f.name}`}
                  name={f.name}
                  required={f.required}
                  type={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text'}
                  className={control}
                />
              )}
            </div>
          ))}
        </div>
        <label className="flex items-start gap-3 text-sm leading-relaxed">
          <input type="checkbox" className="mt-1" />
          <span>{consent}</span>
        </label>
        {/* Invisible until Turnstile decides to challenge; it renders nothing
            for the great majority of visitors. Both renderers find it by this
            attribute — the effect above on a page that hydrates, `SUBMIT_JS`
            on one that does not, never both. */}
        {TURNSTILE_ENABLED && (
          // `suppressHydrationWarning` is belt and braces now that `SUBMIT_JS`
          // stands down on a hydrated document: nothing should fill this div
          // before React reaches it. Kept because the cost is nothing and the
          // failure it hides — a console error on every page — is what the
          // 2026-09-02 diagnosis had to be read through.
          <div
            ref={widgetBox}
            data-dp-turnstile={TURNSTILE_SITE_KEY}
            className="mt-4 min-h-[65px]"
            suppressHydrationWarning
          />
        )}
        {/* In the page from the start and hidden, like `.dp-required`: both
            submit paths reveal it rather than building it, so a page with no
            bundle gets the same message. */}
        {TURNSTILE_ENABLED && (
          <>
            <p
              className={`dp-verifying mt-2 text-sm text-muted-foreground${
                verifying ? '' : ' dp-hide'
              }`}
              role="status"
            >
              {(CHECK_COPY[locale] ?? CHECK_COPY.en).verifying}
            </p>
            <p
              className={`dp-check-needed mt-2 text-sm text-primary${
                checkFailed ? '' : ' dp-hide'
              }`}
            >
              {thanks.checkNote ?? (CHECK_COPY[locale] ?? CHECK_COPY.en).note}
            </p>
          </>
        )}

        {privacy && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {privacy.before}
            <a href={privacy.link.href} className="font-medium text-primary hover:underline">
              {privacy.link.label}
            </a>
            {privacy.after}
          </p>
        )}
        <button type="submit" className="btn-primary" disabled={verifying}>
          {submit}
        </button>
      </form>
      <p id={noteId} className="dp-form-note mt-4 text-sm text-muted-foreground">
        {confirmation}
      </p>
      {/*
        The same thank-you the `sent` branch above renders, but sitting in the
        page from the start and hidden, because on a page with no bundle there is
        no React to render it when the moment comes — `SUBMIT_JS` just unhides
        this and hides the form. Both variants ship: the script shows the
        download wording and the file button only when the reader arrived here
        from a Download button. A hydrated page never reaches it, since React
        replaces the whole box instead.
      */}
      <div id={thanksId} role="status" className="dp-thanks dp-hide space-y-4">
        {thanks.download.map((line) => (
          <p key={line} className="dp-thanks-download dp-hide text-lg leading-relaxed">
            {line}
          </p>
        ))}
        {thanks.contact.map((line) => (
          <p key={line} className="dp-thanks-contact text-lg leading-relaxed">
            {line}
          </p>
        ))}
        <p className="text-base leading-relaxed text-muted-foreground">
          {thanks.help.before}
          <a href={thanks.help.href} className="font-medium text-primary hover:underline">
            {thanks.help.link}
          </a>
          {thanks.help.after}
        </p>
        <p className="dp-thanks-failed dp-hide text-base leading-relaxed text-muted-foreground">
          We could not record that automatically. If you do not hear from us,
          please write to{' '}
          <a
            href={`mailto:${CONTACT_FALLBACK_EMAIL}`}
            className="font-medium text-primary hover:underline"
          >
            {CONTACT_FALLBACK_EMAIL}
          </a>
          .
        </p>
        <a
          href="#"
          target="_blank"
          rel="noopener noreferrer"
          className="dp-thanks-file dp-hide btn-primary"
        >
          {thanks.downloadLabel}
        </a>
      </div>
    </div>
  )
}
